// Таймер отдыха между подходами. Считается от абсолютного времени старта, которое лежит
// в localStorage: сворачивание, перезагрузка и даже закрытие вкладки его не сбрасывают.
// По окончании — navigator.vibrate (один раз; если отдых кончился, пока страница была закрыта
// дольше 10 с, вибрации уже нет), дальше таймер идёт в минус («перебор»), пока его не
// остановят или не начнут новый отдых; забытый перебор сам сбрасывается через 30 минут.

import { useSyncExternalStore } from 'react'

export const DEFAULT_REST_S = 90
const STORAGE_KEY = 'health.restTimer'
const LATE_VIBRATE_MS = 10_000
const VIBRATE_PATTERN = [300, 150, 300]
const OVERTIME_LIMIT_MS = 30 * 60_000

type Stored = { started_at: number; duration_s: number; alerted?: boolean }

export type RestTimerOptions = {
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  key?: string
  now?: () => number
  vibrate?: (pattern: number[]) => unknown
}

export type RestTimerSnapshot = {
  /** Идёт отдых — обратный отсчёт или перебор. */
  running: boolean
  /** Осталось, целых секунд (с округлением вверх). */
  remaining: number
  /** Перебор после окончания, целых секунд; 0, пока идёт обратный отсчёт. */
  overtime: number
  /** На сколько поставлен, секунд. */
  duration: number
}

const IDLE: RestTimerSnapshot = { running: false, remaining: 0, overtime: 0, duration: 0 }

function defaultStorage(): RestTimerOptions['storage'] {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage
  } catch {
    return undefined
  }
}

function defaultVibrate(pattern: number[]): unknown {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return false
  return navigator.vibrate(pattern)
}

export function createRestTimer(options: RestTimerOptions = {}) {
  const storage = options.storage ?? defaultStorage()
  const key = options.key ?? STORAGE_KEY
  const now = options.now ?? (() => Date.now())
  const vibrate = options.vibrate ?? defaultVibrate
  const listeners = new Set<() => void>()
  const scheduleListeners = new Set<(endsAt: number | null) => void>()
  let snapshot: RestTimerSnapshot = IDLE

  const read = (): Stored | null => {
    try {
      const raw = storage?.getItem(key)
      if (!raw) return null
      const value = JSON.parse(raw) as Partial<Stored>
      if (typeof value.started_at !== 'number' || typeof value.duration_s !== 'number') return null
      return { started_at: value.started_at, duration_s: value.duration_s, alerted: value.alerted === true }
    } catch {
      return null
    }
  }
  const write = (value: Stored | null) => {
    try {
      if (value) storage?.setItem(key, JSON.stringify(value))
      else storage?.removeItem(key)
    } catch {
      // хранилище недоступно (приватный режим) — таймер живёт до перезагрузки
    }
  }

  let state = read()

  const endsAt = (s: Stored) => s.started_at + s.duration_s * 1000
  const remainingOf = (s: Stored | null) => (s ? Math.max(0, Math.ceil((endsAt(s) - now()) / 1000)) : 0)
  const overtimeOf = (s: Stored | null) => (s ? Math.max(0, Math.floor((now() - endsAt(s)) / 1000)) : 0)
  const expired = (s: Stored) => now() - endsAt(s) > OVERTIME_LIMIT_MS

  if (state && expired(state)) {
    state = null
    write(null)
  }

  const refresh = () => {
    const next: RestTimerSnapshot = state
      ? { running: true, remaining: remainingOf(state), overtime: overtimeOf(state), duration: state.duration_s }
      : IDLE
    if (
      next.running !== snapshot.running ||
      next.remaining !== snapshot.remaining ||
      next.overtime !== snapshot.overtime ||
      next.duration !== snapshot.duration
    ) {
      snapshot = next
      for (const listener of listeners) listener()
    }
  }

  /** Сообщить подписчикам новый конец отдыха (null — отдыха нет): по нему сервер шлёт push. */
  const announce = () => {
    const at = state ? endsAt(state) : null
    for (const listener of scheduleListeners) listener(at)
  }

  const timer = {
    /** Запустить отдых на `duration` секунд с этого момента. */
    start(duration: number) {
      state = { started_at: now(), duration_s: Math.max(1, Math.round(duration)) }
      write(state)
      refresh()
      announce()
    },
    stop() {
      if (!state) return
      state = null
      write(null)
      refresh()
      announce()
    },
    /** Добавить (или убавить) секунды к текущему отдыху. */
    add(seconds: number) {
      if (!state || remainingOf(state) === 0) return
      state = { ...state, duration_s: Math.max(1, state.duration_s + seconds) }
      write(state)
      refresh()
      announce()
    },
    remaining(): number {
      return remainingOf(state)
    },
    /** Перебор после окончания, секунд. */
    overtime(): number {
      return overtimeOf(state)
    },
    isRunning(): boolean {
      return state !== null
    },
    /** Проверить окончание: вибрация один раз, затем перебор; забытый — сброс. Вызывается по интервалу. */
    tick() {
      if (state && remainingOf(state) === 0) {
        if (expired(state)) {
          state = null
          write(null)
        } else if (!state.alerted) {
          if (now() - endsAt(state) <= LATE_VIBRATE_MS) vibrate(VIBRATE_PATTERN)
          state = { ...state, alerted: true }
          write(state)
        }
      }
      refresh()
    },
    snapshot(): RestTimerSnapshot {
      return snapshot
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    /** Подписка на смену конца отдыха: старт, ±секунды, стоп (не на каждый тик). */
    onSchedule(listener: (endsAt: number | null) => void): () => void {
      scheduleListeners.add(listener)
      return () => scheduleListeners.delete(listener)
    },
  }
  refresh()
  return timer
}

export type RestTimer = ReturnType<typeof createRestTimer>

/** Общий таймер приложения (localStorage, вибрация браузера). */
export const restTimer = createRestTimer()

let interval: ReturnType<typeof setInterval> | null = null
let subscribers = 0

function subscribeTicking(listener: () => void): () => void {
  const unsubscribe = restTimer.subscribe(listener)
  subscribers += 1
  if (interval === null) {
    restTimer.tick()
    interval = setInterval(() => restTimer.tick(), 250)
  }
  return () => {
    unsubscribe()
    subscribers -= 1
    if (subscribers === 0 && interval !== null) {
      clearInterval(interval)
      interval = null
    }
  }
}

/** Состояние таймера для экрана; пока кто-то смотрит, таймер тикает. */
export function useRestTimer(): RestTimerSnapshot & {
  start: (seconds: number) => void
  stop: () => void
  add: (seconds: number) => void
} {
  const snap = useSyncExternalStore(subscribeTicking, restTimer.snapshot, restTimer.snapshot)
  return { ...snap, start: restTimer.start, stop: restTimer.stop, add: restTimer.add }
}
